import { useRef, ChangeEvent } from 'react';
import CardInfos from '@/components/CardInfos';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import {
  ALLOWED_IMAGE_FILE_TYPES,
  MAXIMUM_IMG_SIZE_ALLOWED,
} from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import { cnpjMask, phoneMask } from '@/utils/masks';
import { CameraAltOutlined, PersonOutline } from '@mui/icons-material';
import { Typography, Box, Badge, IconButton, Avatar } from '@mui/material';

interface ICompanyDataProps {
  nextStep: () => void;
}

function validateLogoImage(file: File) {
  if (file.size >= MAXIMUM_IMG_SIZE_ALLOWED) {
    throw Error('Tamanho máximo de arquivo excedido (30 mb)');
  }

  if (!ALLOWED_IMAGE_FILE_TYPES.includes(file.type)) {
    throw Error('Formato não suportado! (São permitidos: PNG, JPG, JPEG)');
  }

  return file;
}

const CompanyData = ({ nextStep }: ICompanyDataProps) => {
  const { defaultMembership, updateUserData, user } = useUser();
  const { setIsLoading } = useLoading();
  const defaultCompany = defaultMembership?.company as ICompany;
  const logoInput = useRef<HTMLInputElement>(null);

  const companyName =
    defaultCompany.display_name ||
    defaultCompany.short_name ||
    defaultCompany.full_name;

  const rowsCompanyInfo = [
    { title: 'Nome fantasia', content: defaultCompany.short_name },
    { title: 'Razão social', content: defaultCompany.full_name },
    {
      title: 'CNPJ',
      content: cnpjMask(defaultCompany.cnpj || ''),
    },
    {
      title: 'Whatsapp',
      content: phoneMask(defaultCompany.phone || ''),
    },
    { title: 'Email', content: defaultCompany.email },
  ];

  const handleChangeLogoImg = async (e: ChangeEvent<HTMLInputElement>) => {
    const [companyData, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const formData = new FormData();
      const logoImageFiles = e.target.files;

      if (!logoImageFiles || logoImageFiles.length === 0) {
        return;
      }
      const validatedLogo = validateLogoImage(logoImageFiles[0]);

      formData.append('full_name', defaultCompany.full_name);
      formData.append('logo', validatedLogo);

      const { data } = await api.patch<ICompany>(
        `/api/companies/${defaultCompany.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      return data;
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast('error', error.message);
      if (logoInput.current) {
        logoInput.current.value = '';
      }
      return;
    }

    updateUserData({
      memberships: user.memberships?.map(membership =>
        membership.company.id === companyData?.id
          ? {
              ...membership,
              company: companyData,
            }
          : membership
      ),
    });
  };

  const isValidUser = user.verified_email && user.verified_cpf;

  const isLastUserConfigured =
    defaultMembership?.company.has_superadmins &&
    defaultMembership?.has_confirmed_configuration;

  const isAdmin =
    defaultMembership?.is_validated && defaultMembership?.is_superadmin;

  const canChangeLogoImage = isValidUser && (isLastUserConfigured || isAdmin);

  return (
    <>
      <Box sx={{ marginBottom: '24px', textAlign: 'center' }}>
        <Badge
          overlap="circular"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          badgeContent={
            <>
              {canChangeLogoImage && (
                <IconButton onClick={() => logoInput.current?.click()}>
                  <Avatar
                    sx={{
                      backgroundColor: 'background.paper',
                      color: 'rgba(0,0,0,0.5)',
                      border: '1px solid',
                    }}
                  >
                    <CameraAltOutlined />
                  </Avatar>
                </IconButton>
              )}
            </>
          }
        >
          <Avatar
            alt="Logo da empresa"
            src={defaultCompany.logo || '/'}
            sx={{
              height: '135px',
              width: '135px',
              border: '1px solid #C7C7C7',
              backgroundColor: '#E9E9E9',
            }}
          >
            {!defaultCompany.logo && (
              <PersonOutline sx={{ fontSize: '48px', color: '#C7C7C7' }} />
            )}
          </Avatar>
        </Badge>

        {canChangeLogoImage && (
          <input
            className="sr-only"
            ref={logoInput}
            type="file"
            data-testid="logo-img-input"
            onChange={handleChangeLogoImg}
            accept={'image/png, image/jpg, image/jpeg'}
          />
        )}

        <Typography variant="h6" component="h3" sx={{ marginTop: '24px' }}>
          {companyName}
        </Typography>
      </Box>

      <CardInfos
        rows={rowsCompanyInfo}
        onClick={nextStep}
        buttonIdentification="edit-company-data-button"
      />
    </>
  );
};

export default CompanyData;
export type { ICompanyDataProps };
